"use client";

import { useT } from "@/lib/i18n";
import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useRouter } from "@/i18n/navigation";
import { useBetaPrefix } from "@/lib/api/prefix.client";
import { useAuth } from "@/app/contexts/AuthContext";
import RunDropZone from "@/app/components/RunDropZone";
import { characterHex } from "@/lib/character-colors";

const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";

interface Run {
  run_hash: string;
  character: string;
  win: boolean;
  was_abandoned: boolean;
  ascension: number;
  floors_reached: number;
  submitted_at: string;
}

interface RunInsight {
  runHash: string;
  character: string;
  win: boolean;
  archetype: {
    name: string;
    win_rate: number;
    share: number;
    similarity: number;
  } | null;
  seedRank: number | null;
  seedTotal: number | null;
}

export default function SubmitRunClient() {
  const t = useT();
  const router = useRouter();
  const bp = useBetaPrefix();
  const { user, loading: authLoading, loginSteam } = useAuth();
  const [error, setError] = useState("");
  const [username, setUsername] = useState("");
  const [runs, setRuns] = useState<Run[]>([]);
  const [runsTotal, setRunsTotal] = useState(0);
  const [runsLoading, setRunsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{
    total: number;
    done: number;
    dupes: number;
    errors: number;
  } | null>(null);
  const [insight, setInsight] = useState<RunInsight | null>(null);
  const uploadInFlight = useRef(false);

  function isValidRunFile(data: any): boolean {
    return (
      data &&
      typeof data === "object" &&
      data.players &&
      data.acts &&
      data.map_point_history &&
      "win" in data
    );
  }

  function diagnoseRunFile(data: any): string {
    if (!data || typeof data !== "object") return "not a JSON object";
    const missing: string[] = [];
    if (!data.players) missing.push("players");
    if (!data.acts) missing.push("acts");
    if (!data.map_point_history) missing.push("map_point_history");
    if (!("win" in data)) missing.push("win");
    return missing.length ? `missing fields: ${missing.join(", ")}` : "unknown";
  }

  async function reportInvalidRuns(
    failures: {
      filename: string;
      reason: string;
      keys?: string[];
      schema?: number;
      build?: string;
    }[],
  ) {
    if (failures.length === 0) return;
    try {
      const summary = failures
        .slice(0, 10)
        .map((f) => {
          let line = `${f.filename}: ${f.reason}`;
          if (f.keys) line += ` [keys: ${f.keys.join(",")}]`;
          if (f.schema) line += ` [schema: ${f.schema}]`;
          if (f.build) line += ` [build: ${f.build}]`;
          return line;
        })
        .join("\n");
      const body =
        failures.length > 10
          ? `${summary}\n... and ${failures.length - 10} more`
          : summary;
      await fetch(`${API}/api/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "Bug",
          contact: "auto-report",
          contents: `Run upload: ${failures.length} invalid out of batch\n\n${body}`,
        }),
      }).catch(() => {});
    } catch {}
  }

  const handleFileUpload = useCallback(
    async (files: FileList) => {
      const total = files.length;
      if (total === 0) return;
      if (uploadInFlight.current) return;
      uploadInFlight.current = true;
      setUploadProgress({ total, done: 0, dupes: 0, errors: 0 });
      setError("");
      setInsight(null);

      let done = 0,
        dupes = 0,
        errors = 0;
      const failures: {
        filename: string;
        reason: string;
        keys?: string[];
        schema?: number;
        build?: string;
      }[] = [];
      const submitUrl = username.trim()
        ? `${API}/api/runs?username=${encodeURIComponent(username.trim())}`
        : `${API}/api/runs`;

      let lastHash: string | null = null;
      let lastResult: any = null;
      let lastCharacter = "";
      let lastWin = false;

      for (const file of Array.from(files)) {
        try {
          const text = await file.text();
          const data = JSON.parse(text);
          if (!isValidRunFile(data)) {
            errors++;
            failures.push({
              filename: file.name,
              reason: diagnoseRunFile(data),
              keys: Object.keys(data).slice(0, 15),
              schema: data?.schema_version,
              build: data?.build_id,
            });
          } else {
            const res = await fetch(submitUrl, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: text,
            });
            const result = await res.json().catch(() => null);
            if (result?.duplicate) {
              dupes++;
              if (result?.run_hash) {
                lastHash = result.run_hash;
                lastResult = result;
                lastCharacter = String(data?.players?.[0]?.character || "");
                lastWin = Boolean(data?.win);
              }
            } else if (!res.ok) {
              errors++;
              failures.push({
                filename: file.name,
                reason: `backend ${res.status}: ${result?.detail || "unknown"}`,
                schema: data?.schema_version,
                build: data?.build_id,
              });
            } else if (result?.run_hash) {
              lastHash = result.run_hash;
              lastResult = result;
              lastCharacter = String(data?.players?.[0]?.character || "");
              lastWin = Boolean(data?.win);
            }
          }
        } catch (e) {
          errors++;
          failures.push({
            filename: file.name,
            reason: `exception: ${e instanceof Error ? e.message : "parse/network error"}`,
          });
        }
        done++;
        setUploadProgress({ total, done, dupes, errors });
      }

      if (failures.length > 0) {
        reportInvalidRuns(failures);
      }

      // If single file uploaded successfully, show the insight card
      // (falling back to the old redirect when there is nothing to show)
      if (total === 1 && errors === 0 && lastHash) {
        if (lastResult?.archetype || lastResult?.seed_rank) {
          setInsight({
            runHash: lastHash,
            character: lastCharacter,
            win: lastWin,
            archetype: lastResult?.archetype || null,
            seedRank: lastResult?.seed_rank ?? null,
            seedTotal: lastResult?.seed_total ?? null,
          });
        } else {
          router.push(`${bp}/runs/${lastHash}`);
        }
      }
      uploadInFlight.current = false;
    },
    [username, bp, router],
  );

  const fetchRuns = useCallback(async () => {
    setRunsLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/runs?page=1&limit=5`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setRuns(data.runs || []);
        setRunsTotal(data.total || 0);
      }
    } catch {
    } finally {
      setRunsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) fetchRuns();
  }, [user, fetchRuns]);

  return (
    <div className="mx-auto max-w-[1400px] px-3 sm:px-5 py-6 space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-2">
          <span className="text-[var(--accent-gold)]">
            {t("Contribute a Run")}
          </span>
        </h1>
        <p className="text-sm text-[var(--text-muted)]">
          {t("submit_tagline")}
        </p>
      </div>

      {/* Sign in prompt */}
      {!authLoading && !user && (
        <div className="rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] p-4 sm:p-5">
          <p className="text-sm text-[var(--text-secondary)] mb-3">
            {t("Sign in to automatically associate runs with your account.")}
          </p>
          <div className="flex flex-wrap gap-2">
            <a
              href={loginSteam}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-card-hover)] transition-colors"
            >
              <svg
                className="w-4 h-4 shrink-0"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658a3.387 3.387 0 0 1 1.912-.593c.064 0 .127.003.19.007l2.862-4.146v-.058a4.533 4.533 0 0 1 4.53-4.53 4.533 4.533 0 0 1 4.53 4.53 4.533 4.533 0 0 1-4.53 4.53h-.106l-4.08 2.91c0 .053.003.107.003.161a3.4 3.4 0 0 1-3.4 3.4 3.404 3.404 0 0 1-3.367-2.936L.256 15.21C1.542 20.2 6.218 24 11.979 24 18.627 24 24 18.627 24 11.979 24 5.373 18.627 0 11.979 0z" />
              </svg>
              Steam
            </a>
          </div>
        </div>
      )}

      {/* Username input (only when not signed in) */}
      {!user && (
        <input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value.slice(0, 32))}
          placeholder={t("Username (optional)")}
          maxLength={32}
          className="px-3 py-2 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-[var(--text-primary)] text-sm focus:outline-none focus:border-[var(--accent-gold)] w-full sm:w-48"
        />
      )}

      <RunDropZone onFiles={handleFileUpload} uploadProgress={uploadProgress} />

      {insight && (
        <div
          className="rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] p-4 sm:p-5"
          style={{
            borderLeft: `3px solid ${characterHex(insight.character) || "var(--accent-gold)"}`,
          }}
        >
          <div className="flex items-center gap-2 mb-3">
            <span
              className={`text-xs px-1.5 py-0.5 rounded ${
                insight.win
                  ? "bg-success/15 text-success"
                  : "bg-[var(--bg-primary)] text-[var(--text-secondary)]"
              }`}
            >
              {insight.win ? t("Victory") : t("Defeat")}
            </span>
            <h2 className="text-sm font-semibold text-[var(--text-primary)]">
              {t("Run uploaded")}
            </h2>
          </div>

          {insight.archetype && (
            <div className="mb-3">
              <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)] mb-1">
                {t("Build")}
              </p>
              <p className="text-lg font-semibold text-[var(--accent-gold)]">
                {insight.archetype.name}
              </p>
              <p className="text-sm text-[var(--text-secondary)]">
                {insight.archetype.win_rate}% {t("community win rate")} ·{" "}
                {insight.archetype.share}% {t("of runs")}
              </p>
            </div>
          )}

          {insight.seedRank != null &&
            insight.seedTotal != null &&
            insight.seedTotal > 0 && (
              <p className="text-sm text-[var(--text-secondary)] mb-3">
                {t("Seed standing")}:{" "}
                <span className="text-[var(--text-primary)] font-medium">
                  #{insight.seedRank} / {insight.seedTotal}
                </span>
              </p>
            )}

          <Link
            href={`${bp}/runs/${insight.runHash}`}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-[var(--accent-gold)] text-on-accent font-medium hover:opacity-90 transition-opacity"
          >
            {t("See the full breakdown")} →
          </Link>
        </div>
      )}

      {error && <p className="text-[var(--color-ironclad)] text-sm">{error}</p>}

      {/* Your Runs (signed in only) */}
      {user && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-[var(--text-primary)]">
              {t("Your Runs")}{" "}
              {runsTotal > 0 && (
                <span className="text-sm font-normal text-[var(--text-tertiary)]">
                  ({runsTotal})
                </span>
              )}
            </h2>
            {runsTotal > 5 && (
              <Link
                href={`${bp}/profile`}
                className="text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
              >
                {t("View all")}
              </Link>
            )}
          </div>

          {runsLoading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="h-12 bg-[var(--bg-card)] rounded animate-pulse"
                />
              ))}
            </div>
          ) : runs.length === 0 ? (
            <p className="text-sm text-[var(--text-secondary)] py-4">
              {t("No runs yet. Upload .run files above to get started.")}
            </p>
          ) : (
            <div className="space-y-1.5">
              {runs.map((run) => (
                <Link
                  key={run.run_hash}
                  href={`${bp}/runs/${run.run_hash}`}
                  className="flex items-center gap-2 sm:gap-3 px-3 py-2.5 rounded-lg bg-[var(--bg-card)] border border-[var(--border-subtle)] text-sm hover:bg-[var(--bg-card-hover)] transition-colors"
                >
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{
                      backgroundColor: characterHex(run.character) || "#888",
                    }}
                  />
                  <span className="font-medium text-[var(--text-primary)] w-20 sm:w-24 truncate">
                    {run.character}
                  </span>
                  <span
                    className={`shrink-0 text-xs px-1.5 py-0.5 rounded ${
                      run.win
                        ? "bg-success/15 text-success"
                        : run.was_abandoned
                          ? "bg-warning/15 text-warning"
                          : "bg-danger/15 text-danger"
                    }`}
                  >
                    {run.win ? "W" : run.was_abandoned ? "A" : "L"}
                  </span>
                  <span className="text-[var(--text-tertiary)] text-xs hidden sm:inline">
                    A{run.ascension}
                  </span>
                  <span className="text-[var(--text-tertiary)] text-xs hidden sm:inline">
                    F{run.floors_reached}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
